import Alert from '@components/v2/ui/Alert/Alert';
import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { SnackbarTestIds } from '../utils/testIds/snackbar.testIds';
import { CustomSnackbarState, SnackbarState } from './snackbar.types';
import { normalizeSnackbarMessage } from './snackbar.utils';

const alignToStackEnd = (content: ReactNode) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>{content}</Box>
);

const getDuration = (isPersistent: boolean) =>
  isPersistent ? Number.POSITIVE_INFINITY : undefined;

const handleMessage = ({ id, title, message, type, loading = false }: SnackbarState) => {
  const normalizedMessage = normalizeSnackbarMessage(message);
  const normalizedTitle = title ? normalizeSnackbarMessage(title) : undefined;

  if (!normalizedTitle && !normalizedMessage) {
    return;
  }

  toast.custom(
    (toastId) =>
      alignToStackEnd(
        <Alert
          variant="floating"
          severity={type}
          title={normalizedTitle || normalizedMessage}
          message={normalizedTitle ? normalizedMessage : undefined}
          isLoading={loading}
          testId={SnackbarTestIds.ROOT}
          onClose={() => toast.dismiss(toastId)}
        />,
      ),
    {
      id: id ?? `${type}:${normalizedTitle ?? ''}:${normalizedMessage}`,
      duration: getDuration(loading),
    },
  );
};

const showCustom = ({ id, content, isPersistent = false, onClose }: CustomSnackbarState) => {
  toast.custom(() => alignToStackEnd(content), {
    id,
    duration: getDuration(isPersistent),
    ...(onClose && { onDismiss: onClose, onAutoClose: onClose }),
  });
};

const snackbarStore = {
  handleMessage,
  showCustom,
  showSuccess: (message: string) => handleMessage({ type: 'success', message }),
  close: (id: string) => toast.dismiss(id),
};

export default snackbarStore;
